import { useMemo, useState } from "react";
import { HiExclamation } from "react-icons/hi";
import { PiCalendar, PiCheckBold, PiClockCounterClockwise, PiStethoscopeBold } from "react-icons/pi";
import toast from "react-hot-toast";

import API from "../../api";
import { capture } from "../../instrument";
import { isRequiredFieldState, TERMINAL_ADMISSION_STATUSES, getPrescriptionWarningLabels } from "../../utils";
import DoctorLookup from "../DoctorLookup";
import PrescriptionCodeLookup from "../PrescriptionCodeLookup";
import RenewalComparisonModal from "./RenewalComparisonModal";
import { TextInput } from "./";

const InlineMetaRow = ({ label, trailing, dataFocusField, attention = false, children }) => (
  <div className="flex items-center gap-2" data-focus-field={dataFocusField}>
    <div className={`field-label flex w-[96px] shrink-0 items-center gap-1${attention ? " text-orange-600" : ""}`}>
      <span className="truncate" title={label}>
        {label}
      </span>
      {trailing}
    </div>
    <div className="min-w-0 flex-1">{children}</div>
  </div>
);

const PrescriptionCard = ({
  prescription,
  index,
  isSelected,
  activeFields,
  selectedCodes,
  setSelectedCodes,
  setContextMenu,
  renewalPopoverCode,
  onRenewalPopoverChange,
  autocompleteSettings,
  loading = false,
  actionsApplied = false,
  incompatibleCodesRefused = [],
  onToggleSelection,
  onAdmissionChange,
  onScrollToImage,
  ProcessingStatusIcon,
  admissionSelected,
  onAdmissionUpdated,
  getAdmissionDetails,
  setLoading,
}) => {
  const warningLabels = useMemo(() => getPrescriptionWarningLabels(prescription), [prescription]);

  const [renewalToCompare, setRenewalToCompare] = useState(null);
  const isLocked = TERMINAL_ADMISSION_STATUSES.includes(admissionSelected?.status);

  const onReplaceWithRenewal = async () => {
    if (!admissionSelected?._id || !prescription._id || !renewalToCompare?.key) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("renewalKey", renewalToCompare.key);
      const createResponse = await API.postFormData(`/admin/admission/${admissionSelected._id}/prescription`, formData);

      if (!createResponse.ok) throw new Error(createResponse.message || "Erreur lors de la création du renouvellement");

      const deleteResponse = await API.delete(`/admin/admission/${admissionSelected._id}/prescription/${prescription._id}`);

      if (!deleteResponse.ok) throw new Error(deleteResponse.message || "Erreur lors de la suppression de l'ancienne ordonnance");

      onAdmissionUpdated(deleteResponse.data);
      setRenewalToCompare(null);
      toast.success("Ordonnance remplacée par le renouvellement");
    } catch (error) {
      capture(error);
      toast.error(error.message || "Une erreur est survenue");
      await getAdmissionDetails(admissionSelected._id);
    }

    setLoading(false);
  };

  const processingIcon = (type, referenceId) => (ProcessingStatusIcon ? <ProcessingStatusIcon type={type} referenceId={referenceId} className="h-3 w-3" /> : null);

  const isMissing = (state, filled) => isRequiredFieldState(state) && !filled;

  return (
    <div onFocus={() => onScrollToImage?.(`prescription-${prescription._id}`)}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="section-title">
          <span>Ordonnance {index + 1}</span>
          {prescription.type === "RENEWAL" && <span className="chip border-primary-200 bg-primary-50 text-primary-700">Renouvellement</span>}
        </h3>
        {!isLocked && (
          <button
            type="button"
            aria-label={isSelected ? "Désélectionner l'ordonnance" : "Sélectionner l'ordonnance"}
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelection();
            }}
            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-all ${
              isSelected ? "border-primary-500 bg-primary-500 text-white shadow-sm" : "border-gray-300 bg-white text-transparent hover:border-primary-300 hover:text-primary-300"
            }`}
          >
            <PiCheckBold className="h-3 w-3" />
          </button>
        )}
      </div>

      <div className={`overflow-hidden rounded-lg border bg-white transition-colors duration-200 ${isSelected ? "border-primary-300 ring-1 ring-primary-300" : "border-gray-100"}`}>
        {warningLabels.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-gray-100 bg-gray-50 px-4 py-2 text-[11px]">
            {warningLabels.map((label) => (
              <span key={label} className="inline-flex items-center gap-1.5 font-semibold text-red-700">
                <HiExclamation className="h-4 w-4 shrink-0 text-red-500" />
                {label}
              </span>
            ))}
          </div>
        )}

        <div className="px-4 py-3">
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-6 flex flex-col gap-1 border-r border-gray-100 pr-3 lg:col-span-5">
              <InlineMetaRow
                label="Prescription"
                trailing={processingIcon("PRESCRIPTION_DATE", prescription._id)}
                dataFocusField={`prescriptions.${index}.date`}
                attention={isMissing(activeFields?.prescription?.date, !!prescription.date)}
              >
                <TextInput
                  inRow
                  fieldState={activeFields?.prescription?.date}
                  value={prescription.date instanceof Date ? prescription.date.toISOString().split("T")[0] : prescription.date || ""}
                  onChange={(e) => onAdmissionChange([{ field: `prescriptions.${prescription._id}.date`, value: e.target.value }])}
                  placeholder="Date de prescription"
                  type="date"
                  disabled={!isLocked && prescription.type === "RENEWAL"}
                />
              </InlineMetaRow>
              <InlineMetaRow
                label="Médecin"
                trailing={processingIcon("DOCTOR", prescription._id)}
                dataFocusField={`prescriptions.${index}.doctor`}
                attention={isMissing(activeFields?.prescription?.doctor, autocompleteSettings.doctor ? !!prescription.doctor?.code : !!prescription.doctor?.RPPS)}
              >
                <DoctorLookup
                  initialDoctor={prescription.doctor}
                  onDoctorSelect={(doctor) => onAdmissionChange([{ field: `prescriptions.${prescription._id}.doctor`, value: doctor }])}
                  onDoctorRemove={() => onAdmissionChange([{ field: `prescriptions.${prescription._id}.doctor`, value: null }])}
                  autocompleteEnabled={autocompleteSettings.doctor}
                  fieldState={activeFields?.prescription?.doctor}
                  disabled={!isLocked && prescription.type === "RENEWAL"}
                />
              </InlineMetaRow>
              <InlineMetaRow
                label="ID externe"
                dataFocusField={`prescriptions.${index}.hprim.silPrescriptionNumber`}
                attention={isMissing(activeFields?.prescription?.silPrescriptionNumber, !!prescription.hprim?.silPrescriptionNumber)}
              >
                <TextInput
                  inRow
                  fieldState={activeFields?.prescription?.silPrescriptionNumber}
                  value={prescription.hprim?.silPrescriptionNumber || ""}
                  onChange={(e) => onAdmissionChange([{ field: `prescriptions.${prescription._id}.hprim.silPrescriptionNumber`, value: e.target.value }])}
                  placeholder="Ajouter un ID externe"
                  disabled={prescription.type === "RENEWAL"}
                />
              </InlineMetaRow>
            </div>
            <div className="col-span-6 flex flex-col lg:col-span-7">
              <div className="field-label mb-1.5">Analyses</div>
              <PrescriptionCodeLookup
                ghost
                prescription={prescription}
                onAdmissionChange={onAdmissionChange}
                selectedCodes={selectedCodes}
                setSelectedCodes={setSelectedCodes}
                setContextMenu={setContextMenu}
                renewalPopoverCode={renewalPopoverCode}
                onRenewalPopoverChange={onRenewalPopoverChange}
                locked={isLocked}
                icon={(actId) => (ProcessingStatusIcon ? <ProcessingStatusIcon type="ACT" referenceId={actId} /> : null)}
                actionsApplied={actionsApplied}
                incompatibleCodesRefused={incompatibleCodesRefused}
              />
            </div>
          </div>

          {!isLocked && admissionSelected?.availableRenewals?.length > 0 && prescription.type !== "RENEWAL" && (
            <div className="mt-3 border-t border-gray-100 pt-3">
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Renouvellements</div>
              <div className="flex flex-col gap-1.5">
                {admissionSelected?.availableRenewals.map((renewal) => (
                  <button
                    key={renewal.key}
                    type="button"
                    onClick={() => setRenewalToCompare(renewal)}
                    className="group flex items-center justify-between gap-3 rounded-lg border border-primary-200/60 bg-gradient-to-r from-primary-50 to-primary-100/50 px-3 py-2 text-xs text-primary-700 transition-all duration-200 hover:border-primary-300 hover:from-primary-100 hover:to-primary-200/50 hover:shadow-sm"
                    disabled={loading}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <PiClockCounterClockwise className="h-3.5 w-3.5 shrink-0 text-primary-500" />
                      <span className="truncate font-medium">{renewal.acts?.map((a) => a.codes.join(", ")).join(", ") || "Renouvellement"}</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-3 text-gray-500">
                      {renewal.doctor?.lastName && (
                        <span className="flex items-center gap-1">
                          <PiStethoscopeBold className="h-3 w-3" />
                          Dr {renewal.doctor.lastName}
                        </span>
                      )}
                      {renewal.date && (
                        <span className="flex items-center gap-1">
                          <PiCalendar className="h-3 w-3" />
                          {new Date(renewal.date).toLocaleDateString("fr-FR")}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {renewalToCompare && (
        <RenewalComparisonModal
          prescription={prescription}
          renewal={renewalToCompare}
          loading={loading}
          onClose={() => setRenewalToCompare(null)}
          onConfirm={onReplaceWithRenewal}
        />
      )}
    </div>
  );
};

export default PrescriptionCard;
